import { KeyboardEvent, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CheckIcon from "@mui/icons-material/Check";
import { format } from "date-fns";
import { formatDateBR } from "../../shared/ui/format";
import { AdvanceRow } from "./columns";

export type PaymentDateCellProps = {
  row: Pick<AdvanceRow, "id" | "payment_date">;
  canWrite: boolean;
  onSave: (id: number, isoDate: string) => Promise<void>;
};

export default function PaymentDateCell({ row, canWrite, onSave }: PaymentDateCellProps) {
  const original = (row.payment_date || "").slice(0, 10);
  const [value, setValue] = useState(original);
  const [saving, setSaving] = useState(false);
  // O DataGrid reaproveita a célula: quando a lista recarrega com a data salva, o campo acompanha.
  useEffect(() => setValue(original), [original]);

  if (!canWrite) return <span>{formatDateBR(row.payment_date, "—")}</span>;

  const canSave = Boolean(value) && value !== original && !saving;
  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(row.id, value);
    } finally {
      setSaving(false);
    }
  };
  // O DataGrid captura teclas (espaço/setas): a célula para a propagação; Enter salva.
  const onKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Enter") void save();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <TextField
        type="date"
        size="small"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        inputProps={{ min: format(new Date(), "yyyy-MM-dd"), "aria-label": `Data de pagamento do pacote #${row.id}` }}
        sx={{ width: 150, "& input": { py: "6px", fontSize: 13 } }}
      />
      <Tooltip title="Salvar data">
        <span>
          <IconButton aria-label={`Salvar data de pagamento do pacote #${row.id}`} color="primary" disabled={!canSave} onClick={save} sx={{ width: 40, height: 40, bgcolor: canSave ? "#e0f7fc" : undefined }}>
            {saving ? <CircularProgress size={18} /> : <CheckIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
